// 多螢幕（LIVE-10，SPEC #vms-web-live10）：同一個瀏覽器設定檔的 VMS 視窗之間用 BroadcastChannel `kinio-vms` 同步。
//   - 登入 / 登出：任一視窗登出 → 全部回登入頁；一個視窗登入 → 其他還在登入頁的自己換 token（同一個 BFF cookie）
//   - 操作活動：每 30 秒最多廣播一次；閒置登出與 LIVE-12 中繼閒置都看所有視窗的最後活動
//   - 解碼預算（LIVE-03）整台共用：每個視窗每 2 秒廣播即時格數；先開的視窗優先，後開的只能用剩下的
//   - 警報視窗（LIVE-09）：只有一個視窗彈警報；有人設成「這個視窗跳警報」就是它，否則（或它關了）是看得見的視窗裡最早開的
//   - 存活：每 2 秒心跳、5 秒沒聲音算關了；隱藏的視窗（瀏覽器會節流計時器）放寬到 75 秒，顯示 / 隱藏時馬上補一次心跳
// 不支援 BroadcastChannel 的瀏覽器：只有自己一個視窗，行為照舊。

export type MultiMsg =
  | { t: 'beat'; id: string; opened: number; live: number; popout: boolean; wall?: string; hidden?: boolean; claimed?: string; claimedAt?: number }
  | { t: 'bye'; id: string }
  | { t: 'logout' }
  | { t: 'login' }
  | { t: 'activity'; at: number }
  | { t: 'alarm-window'; id: string; at: number };

export interface Peer {
  id: string;
  opened: number;
  live: number;
  seen: number;
  popout: boolean;
  wall?: string;
  /** 隱藏（最小化 / 背景分頁）：瀏覽器會把計時器節流到每分鐘一次，存活看得寬一點、不當預設警報視窗 */
  hidden?: boolean;
}

export const BEAT_MS = 2_000;
export const PEER_TTL_MS = 5_000;
/** 隱藏的視窗：Chromium 對隱藏超過 5 分鐘的頁面把 setInterval 節流到每分鐘一次。 */
export const HIDDEN_TTL_MS = 75_000;

function alive(p: Peer, now: number): boolean {
  return now - p.seen <= (p.hidden ? HIDDEN_TTL_MS : PEER_TTL_MS);
}
export const ACTIVITY_EVERY_MS = 30_000;
/** 啟動握手：送出第一個心跳後等其他視窗回應的時間。 */
export const STARTUP_MS = 400;

/** 先開的視窗用掉的即時格數之後，這個視窗還能用多少（不小於 0）。 */
export function sharedBudget(budget: number, me: { id: string; opened: number }, peers: Peer[], now: number): number {
  const before = peers.filter((p) => p.id !== me.id && alive(p, now) && (p.opened < me.opened || (p.opened === me.opened && p.id < me.id)));
  return Math.max(0, budget - before.reduce((s, p) => s + p.live, 0));
}

/** 誰是警報視窗：有人宣告且還活著就是它；否則看得見的視窗裡最早開的；都隱藏才看全部裡最早開的（含自己）。 */
export function alarmWindow(me: { id: string; opened: number; hidden?: boolean }, peers: Peer[], claimed: string | null, now: number): string {
  const all = [{ ...me, hidden: !!me.hidden }, ...peers.filter((p) => p.id !== me.id && alive(p, now))];
  if (claimed && all.some((p) => p.id === claimed)) return claimed;
  const visible = all.filter((p) => !p.hidden);
  return (visible.length ? visible : all).sort((a, b) => a.opened - b.opened || (a.id < b.id ? -1 : 1))[0].id;
}

/** 警報視窗宣告的合併：新的贏，同一毫秒比 id（每個視窗結論一致）。 */
export function mergeClaim(cur: { id: string | null; at: number }, inc: { id?: string | null; at?: number }): { id: string | null; at: number } {
  if (!inc.id || inc.at == null) return cur;
  if (inc.at > cur.at || (inc.at === cur.at && inc.id > (cur.id ?? ''))) return { id: inc.id, at: inc.at };
  return cur;
}

type Handler = (m: MultiMsg) => void;

/** 這個視窗的多螢幕狀態（單例）。 */
class MultiScreen {
  readonly id = Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
  readonly opened = Date.now();
  readonly popout: boolean;
  readonly wall: string | undefined;
  private ch: BroadcastChannel | null = null;
  private peers = new Map<string, Peer>();
  private handlers = new Set<Handler>();
  private myLive = 0;
  private claimed: string | null = null;
  private claimedAt = 0;
  private lastActivitySent = 0;
  private timer: ReturnType<typeof setInterval> | null = null;
  private listeners = new Set<() => void>();
  private started = 0;
  /** 已送出 bye（視窗關掉或進 bfcache）：不再送心跳，免得關閉時的 visibilitychange 又把自己登記成「隱藏但活著」75 秒。 */
  private gone = false;

  constructor() {
    const q = typeof location !== 'undefined' ? new URLSearchParams(location.search) : new URLSearchParams();
    this.popout = q.get('popout') === '1';
    this.wall = q.get('wall') ?? undefined;
  }

  start() {
    if (this.ch || typeof BroadcastChannel === 'undefined') return;
    this.ch = new BroadcastChannel('kinio-vms');
    this.ch.onmessage = (e: MessageEvent<MultiMsg>) => this.receive(e.data);
    this.started = Date.now();
    this.beat();
    setTimeout(() => this.notify(), STARTUP_MS + 50); // 啟動握手結束：重算警報視窗與預算
    this.timer = setInterval(() => this.beat(), BEAT_MS);
    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', () => {
        this.gone = true;
        this.post({ t: 'bye', id: this.id });
      });
      // 從 bfcache 回來：重新出現在其他視窗
      window.addEventListener('pageshow', (e) => {
        if ((e as PageTransitionEvent).persisted && this.gone) {
          this.gone = false;
          this.beat();
        }
      });
    }
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', () => this.beat()); // 顯示 / 隱藏馬上告訴別的視窗
  }

  private receive(m: MultiMsg) {
    if (!m || typeof m !== 'object') return;
    if (m.t === 'beat') {
      const known = this.peers.has(m.id);
      this.peers.set(m.id, { id: m.id, opened: m.opened, live: m.live, seen: Date.now(), popout: m.popout, wall: m.wall, hidden: m.hidden });
      this.learnClaim(m.claimed, m.claimedAt); // 心跳也帶目前的宣告：後開 / 重新整理的視窗握手內就學到
      if (!known && m.id !== this.id) this.beat(); // 新視窗：馬上回一次，不用等下一個 2 秒（對方已認得我，不會來回）
    }
    if (m.t === 'bye') this.peers.delete(m.id);
    if (m.t === 'alarm-window') this.learnClaim(m.id, m.at);
    for (const h of this.handlers) h(m);
    this.notify();
  }

  private learnClaim(id?: string | null, at?: number) {
    const next = mergeClaim({ id: this.claimed, at: this.claimedAt }, { id, at });
    this.claimed = next.id;
    this.claimedAt = next.at;
  }

  post(m: MultiMsg) {
    this.ch?.postMessage(m);
  }

  private beat() {
    if (this.gone) return;
    this.post({ t: 'beat', id: this.id, opened: this.opened, live: this.myLive, popout: this.popout, wall: this.wall, hidden: this.hidden(), claimed: this.claimed ?? undefined, claimedAt: this.claimed ? this.claimedAt : undefined });
    const now = Date.now();
    for (const [k, p] of this.peers) if (!alive(p, now)) this.peers.delete(k);
    this.notify();
  }

  on(h: Handler): () => void {
    this.handlers.add(h);
    return () => this.handlers.delete(h);
  }

  /** 狀態變了（peer 來去、宣告警報視窗）就通知（React 訂閱用）。 */
  subscribe(f: () => void): () => void {
    this.listeners.add(f);
    return () => this.listeners.delete(f);
  }

  private notify() {
    for (const f of this.listeners) f();
  }

  peerList(): Peer[] {
    return [...this.peers.values()];
  }

  /** 這個視窗目前的即時格數（影像牆每次重算後告訴這裡）。 */
  setLive(n: number) {
    if (n !== this.myLive) {
      this.myLive = n;
      this.beat();
    }
  }

  /** 剛開的視窗還在等別的視窗回應（STARTUP_MS 內）：先不轉即時、不彈警報，免得跟既有的視窗重複。 */
  warming(): boolean {
    return !!this.ch && Date.now() - this.started < STARTUP_MS;
  }

  budgetFor(budget: number): number {
    return this.warming() ? 0 : sharedBudget(budget, this, this.peerList(), Date.now());
  }

  private hidden(): boolean {
    return typeof document !== 'undefined' && document.visibilityState === 'hidden';
  }

  isAlarmWindow(): boolean {
    return !this.warming() && alarmWindow({ id: this.id, opened: this.opened, hidden: this.hidden() }, this.peerList(), this.claimed, Date.now()) === this.id;
  }

  claimAlarm() {
    this.claimed = this.id;
    this.claimedAt = Date.now();
    this.post({ t: 'alarm-window', id: this.id, at: this.claimedAt });
    this.notify();
  }

  /** 本機有操作：最多每 30 秒廣播一次。 */
  activity(now = Date.now()) {
    if (now - this.lastActivitySent >= ACTIVITY_EVERY_MS) {
      this.lastActivitySent = now;
      this.post({ t: 'activity', at: now });
    }
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.ch?.close();
    this.ch = null;
  }
}

export const multiScreen = new MultiScreen();

// ---- 彈出與還原（主視窗記住哪面牆在哪個螢幕；localStorage，是工作站設定、不跟帳號走） ----

export interface PopoutSpot {
  wall: string;
  left?: number;
  top?: number;
  width?: number;
  height?: number;
  screen?: string;
}

const SPOTS_KEY = 'kinio.vms.multiscreen.v1';

export function loadSpots(): PopoutSpot[] {
  try {
    const v = JSON.parse(localStorage.getItem(SPOTS_KEY) ?? '[]') as PopoutSpot[];
    return Array.isArray(v) ? v.filter((x) => x && typeof x.wall === 'string') : [];
  } catch {
    return [];
  }
}

export function saveSpots(v: PopoutSpot[]) {
  try {
    localStorage.setItem(SPOTS_KEY, JSON.stringify(v.slice(-8)));
  } catch {
    /* 無 storage */
  }
}

export function forgetSpot(wall: string) {
  saveSpots(loadSpots().filter((s) => s.wall !== wall));
  try {
    localStorage.removeItem(layoutKey('kinio.vms.liveLayout.v1', wall)); // 關掉的牆的版面一起清
  } catch {
    /* 無 storage */
  }
}

/** 影像牆版面的儲存鍵：主視窗沿用舊鍵；彈出的每面牆各一份。 */
export function layoutKey(base: string, wall = multiScreen.wall): string {
  return wall ? `${base}:${wall}` : base;
}

export interface ScreenInfo {
  label: string;
  left: number;
  top: number;
  width: number;
  height: number;
  current: boolean;
}

/** Window Management API（Chromium）：列出螢幕；不支援或使用者拒絕 → null。 */
export async function listScreens(): Promise<ScreenInfo[] | null> {
  const w = window as unknown as { getScreenDetails?: () => Promise<{ screens: ScreenDetailed[]; currentScreen: ScreenDetailed }> };
  if (typeof w.getScreenDetails !== 'function') return null;
  try {
    const d = await w.getScreenDetails();
    return d.screens.map((s, i) => ({
      label: s.label || `#${i + 1}`,
      left: s.availLeft,
      top: s.availTop,
      width: s.availWidth,
      height: s.availHeight,
      current: s === d.currentScreen,
    }));
  } catch {
    return null;
  }
}

interface ScreenDetailed {
  label: string;
  availLeft: number;
  availTop: number;
  availWidth: number;
  availHeight: number;
}

/** 開一面牆的視窗；回 false = 瀏覽器擋了彈出視窗。 */
export function openWall(spot: PopoutSpot): boolean {
  const feat = spot.width && spot.height ? `popup,left=${spot.left ?? 0},top=${spot.top ?? 0},width=${spot.width},height=${spot.height}` : 'popup,width=1280,height=800';
  const w = window.open(`/live?popout=1&wall=${encodeURIComponent(spot.wall)}`, 'kinio-wall-' + spot.wall, feat);
  return !!w;
}

export function newWallId(): string {
  return 'w' + Math.random().toString(36).slice(2, 8);
}
